import {
  AuthErrorCode,
  CredentialSourceKind,
  DEFAULT_HOST_TIMEOUT_MS,
  HostMessageType,
  SessionEndReason,
} from '../auth-core.const';
import { Credential, Unsubscribe } from '../auth-core.types';
import { summariseJwt } from '../helpers/jwt.helpers';
import { withTimeout } from '../helpers/single-flight.helpers';
import {
  ClockPort,
  HostMessageEvent,
  LoggerPort,
  MessagePort,
} from '../ports/ports.types';

import { CredentialSource } from './credential-source.types';

export type HostCredentialSourceOptions = {
  /**
   * Origins permitted to hand us a token. **Required and never defaulted** —
   * an empty allowlist accepts nothing, which is the only safe failure mode.
   */
  allowedOrigins: string[];
  messagePort: MessagePort;
  clock: ClockPort;
  logger: LoggerPort;
  /** How long to wait for the host to answer. */
  timeoutMs?: number;
  /** When set, a token whose `iss` differs is rejected. */
  expectedIssuer?: string;
};

type HostMessage = {
  type: string;
  token?: unknown;
  requestId?: unknown;
};

/**
 * Receives a credential from an embedding host over a verified postMessage
 * channel, and asks the host for a new one when it expires.
 *
 * ## Why this shape
 *
 * The mechanism this replaces adopts `?access_token=` from the URL with no
 * origin check, no signature check and no expiry handling, then — when the
 * token expires — falls back to an OIDC silent sign-in. Inside an iframe that
 * silently authenticates as *whoever owns the browser's IdP session*, which is
 * a different user from the one the host intended. So:
 *
 * - the token arrives over a channel we can attribute to a specific origin
 *   **and** to our actual parent window;
 * - we read `exp`, which is what lets `AuthClient` schedule renewal here at all
 *   — expiry becomes a scheduled event rather than a surprise 401;
 * - renewal asks **the host**, and there is deliberately no OIDC fallback.
 */
export class HostCredentialSource implements CredentialSource {
  readonly kind = CredentialSourceKind.Host;

  private readonly options: Required<
    Omit<HostCredentialSourceOptions, 'expectedIssuer'>
  > &
    Pick<HostCredentialSourceOptions, 'expectedIssuer'>;
  private readonly listeners = new Set<
    (credential: Credential | null, reason?: SessionEndReason) => void
  >();
  private readonly pending = new Set<(credential: Credential) => void>();

  private credential: Credential | null = null;
  private messageHandler: ((event: HostMessageEvent) => void) | null = null;
  private requestCounter = 0;

  constructor(options: HostCredentialSourceOptions) {
    this.options = { timeoutMs: DEFAULT_HOST_TIMEOUT_MS, ...options };
  }

  async init(): Promise<Credential | null> {
    this.attach();
    this.broadcast({ type: HostMessageType.Ready });

    try {
      return await this.awaitToken();
    } catch (error) {
      this.options.logger.warn('No token from host during init', { error });
      return null;
    }
  }

  getCredential(): Credential | null {
    return this.credential;
  }

  async renew(): Promise<Credential | null> {
    this.attach();
    const counter = (this.requestCounter += 1);
    const requestId = `r${counter}`;
    this.broadcast({ type: HostMessageType.TokenRequest, requestId });
    return this.awaitToken();
  }

  async revoke(): Promise<void> {
    this.setCredential(null);
  }

  subscribe(
    listener: (credential: Credential | null, reason?: SessionEndReason) => void,
  ): Unsubscribe {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  dispose(): void {
    if (this.messageHandler) {
      this.options.messagePort.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }
    this.listeners.clear();
    this.pending.clear();
  }

  /**
   * Interactive sign-in is meaningless here: we have no IdP relationship, and
   * attempting one inside an iframe is the identity-swap bug this class exists
   * to prevent. Failing loudly is the correct behaviour.
   */
  async signIn(): Promise<void> {
    throw Object.assign(new Error('A host-provided session cannot sign in'), {
      code: AuthErrorCode.NotSupported,
    });
  }

  async signOut(): Promise<void> {
    throw Object.assign(new Error('A host-provided session cannot sign out'), {
      code: AuthErrorCode.NotSupported,
    });
  }

  private attach(): void {
    if (this.messageHandler) return;

    this.messageHandler = (event) => this.handleMessage(event);
    this.options.messagePort.addEventListener('message', this.messageHandler);
  }

  /** Announces to every allowed origin. Never uses `*` as a target. */
  private broadcast(message: HostMessage): void {
    for (const origin of this.options.allowedOrigins) {
      this.options.messagePort.postToHost(message, origin);
    }
  }

  private handleMessage(event: HostMessageEvent): void {
    const message = asHostMessage(event.data);
    if (!message) return;
    if (!this.isTrusted(event, message.type)) return;

    if (message.type === HostMessageType.SignedOut) {
      this.setCredential(null, SessionEndReason.ProviderSignedOut);
      return;
    }
    if (message.type !== HostMessageType.TokenResponse) return;

    const credential = this.toCredential(message.token);
    if (!credential) return;

    this.setCredential(credential);
    this.resolvePending(credential);
  }

  /** Both checks matter — see {@link MessagePort.isHostWindow}. */
  private isTrusted(event: HostMessageEvent, messageType: string): boolean {
    if (!this.options.allowedOrigins.includes(event.origin)) {
      this.options.logger.warn('Rejected host message from untrusted origin', {
        origin: event.origin,
        messageType,
        code: AuthErrorCode.UntrustedOrigin,
      });
      return false;
    }

    if (!this.options.messagePort.isHostWindow(event.source)) {
      this.options.logger.warn('Rejected host message from a non-parent window', {
        origin: event.origin,
        messageType,
        code: AuthErrorCode.UntrustedOrigin,
      });
      return false;
    }

    return true;
  }

  private toCredential(token: unknown): Credential | null {
    if (typeof token !== 'string' || token.trim().length === 0) {
      this.options.logger.warn('Host sent an empty token', {
        code: AuthErrorCode.MalformedToken,
      });
      return null;
    }

    const summary = summariseJwt(token);
    if (!summary?.expiresAt || !summary.subject) {
      this.options.logger.warn('Host token is not a JWT with exp and sub', {
        code: AuthErrorCode.MalformedToken,
      });
      return null;
    }

    if (this.options.expectedIssuer && summary.issuer !== this.options.expectedIssuer) {
      this.options.logger.warn('Host token was issued by an unexpected issuer', {
        code: AuthErrorCode.MalformedToken,
      });
      return null;
    }

    if (summary.expiresAt * 1000 <= this.options.clock.now()) {
      this.options.logger.warn('Host sent an already-expired token', {
        code: AuthErrorCode.MalformedToken,
      });
      return null;
    }

    return {
      accessToken: token,
      expiresAt: summary.expiresAt,
      subject: summary.subject,
      claims: summary.claims,
    };
  }

  private awaitToken(): Promise<Credential> {
    const received = new Promise<Credential>((resolve) => {
      this.pending.add(resolve);
    });

    return withTimeout(received, this.options.timeoutMs, () =>
      Object.assign(new Error('The host did not provide a token in time'), {
        code: AuthErrorCode.HostTimeout,
      }),
    );
  }

  private resolvePending(credential: Credential): void {
    for (const resolve of [...this.pending]) {
      resolve(credential);
    }
    this.pending.clear();
  }

  private setCredential(credential: Credential | null, reason?: SessionEndReason): void {
    this.credential = credential;
    for (const listener of [...this.listeners]) {
      listener(credential, reason);
    }
  }
}

function asHostMessage(data: unknown): HostMessage | null {
  if (typeof data !== 'object' || data === null) return null;
  const candidate = data as { type?: unknown };
  if (typeof candidate.type !== 'string') return null;
  if (!candidate.type.startsWith('xemplo:auth:')) return null;
  return data as HostMessage;
}
